import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ShareFormButton } from '@/components/ShareFormButton';
import { ArrowLeft, CheckCircle, Building2, Loader2, Search, FileCheck2, Radio, Users } from 'lucide-react';
import { getImageUrl } from '@/lib/imageUtils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useHoldedIntegracion } from '@/hooks/useHoldedIntegracion';
import { useHoldedContactLookup, type HoldedContactMatch } from '@/hooks/useHoldedContactLookup';
import { HoldedContactMatchCard } from '@/components/clientes/HoldedContactMatchCard';
import { ClienteDuplicadoLocalCard } from '@/components/clientes/ClienteDuplicadoLocalCard';
import { normalizeNif } from '@/lib/holded/normalizeNif';

const SERVICIOS = [
  { id: 'alarma', label: 'Alarma (con conexión a CRA)' },
  { id: 'pci', label: 'PCI (Protección contra incendios)' },
  { id: 'cctv', label: 'CCTV / Videovigilancia' },
  { id: 'accesos', label: 'Control de accesos' },
  { id: 'otros', label: 'Otros' },
] as const;

const contactoSchema = z.object({
  nombre: z.string().optional(),
  telefono: z.string().optional(),
});

const formSchema = z.object({
  nombre: z.string().min(1, 'Nombre completo es requerido'),
  direccion: z.string().optional(),
  piso: z.string().optional(),
  puerta: z.string().optional(),
  codigo_postal: z.string().optional(),
  poblacion: z.string().optional(),
  telefono: z.string().optional(),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  nombre_fiscal: z.string().optional(),
  direccion_fiscal: z.string().optional(),
  nif_cif: z.string().min(1, 'NIF/CIF es requerido'),
  email_facturacion: z.string().email('Email de facturación inválido').optional().or(z.literal('')),
  telefono_facturacion: z.string().optional(),
  servicios: z.array(z.string()).default([]),
  numero_abonado: z.string().optional(),
  palabra_clave: z.string().optional(),
  palabra_coaccion: z.string().optional(),
  contactos: z.array(contactoSchema).default([]),
});

type FormData = z.infer<typeof formSchema>;

export default function AltaClienteCRA() {
  const { toast } = useToast();
  const { perfil, empresa } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [createdClienteId, setCreatedClienteId] = useState<string | null>(null);
  const [publicEmpresa, setPublicEmpresa] = useState<any>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [linkedHoldedContact, setLinkedHoldedContact] = useState<HoldedContactMatch | null>(null);
  const [ignoreHoldedMatch, setIgnoreHoldedMatch] = useState(false);
  const [duplicadoLocal, setDuplicadoLocal] = useState<{ id: string; nombre: string; nif_cif: string | null } | null>(null);

  const isLoggedIn = !!perfil;
  const empresaIdFromUrl = searchParams.get('empresa_id');
  const empresaId = perfil?.empresa_id || empresaIdFromUrl;
  const { activa: holdedActiva } = useHoldedIntegracion();

  useEffect(() => {
    const fetchEmpresaInfo = async () => {
      if (empresaId && !empresa) {
        try {
          const { data, error } = await supabase
            .from('empresas')
            .select('nombre, logo_url')
            .eq('id', empresaId)
            .single();
          if (!error && data) setPublicEmpresa(data);
        } catch (err) {
          console.error('Error fetching empresa:', err);
        } finally {
          setIsInitialLoading(false);
        }
      } else {
        setIsInitialLoading(false);
      }
    };
    fetchEmpresaInfo();
  }, [empresaId, empresa]);

  const displayLogo = getImageUrl(empresa?.logo_url || publicEmpresa?.logo_url);
  const displayNombreEmpresa = empresa?.nombre || publicEmpresa?.nombre;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nombre: '', direccion: '', piso: '', puerta: '',
      codigo_postal: '', poblacion: '', telefono: '', email: '',
      nombre_fiscal: '', direccion_fiscal: '', nif_cif: '',
      email_facturacion: '', telefono_facturacion: '',
      servicios: [],
      numero_abonado: '', palabra_clave: '', palabra_coaccion: '',
      contactos: [{ nombre: '', telefono: '' }, { nombre: '', telefono: '' }, { nombre: '', telefono: '' }],
    },
  });

  const serviciosWatch = form.watch('servicios') || [];
  const tieneAlarma = serviciosWatch.includes('alarma');

  const nifCifWatch = form.watch('nif_cif') || '';
  const normalizedNif = useMemo(() => normalizeNif(nifCifWatch), [nifCifWatch]);
  const lookupEnabled = !!perfil && holdedActiva && !linkedHoldedContact && !ignoreHoldedMatch;
  const { status: lookupStatus, contact: holdedContact } = useHoldedContactLookup(nifCifWatch, lookupEnabled);

  useEffect(() => {
    if (!perfil?.empresa_id || !normalizedNif || normalizedNif.length < 4) {
      setDuplicadoLocal(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from('clientes')
        .select('id, nombre, nif_cif')
        .eq('empresa_id', perfil.empresa_id)
        .eq('activo', true)
        .not('nif_cif', 'is', null);
      if (cancelled) return;
      const hit = (data || []).find((c: any) => normalizeNif(c.nif_cif) === normalizedNif);
      setDuplicadoLocal(hit ? { id: hit.id, nombre: hit.nombre, nif_cif: hit.nif_cif } : null);
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [normalizedNif, perfil?.empresa_id]);

  const toggleServicio = (id: string) => {
    const current = form.getValues('servicios') || [];
    if (current.includes(id)) {
      form.setValue('servicios', current.filter((s) => s !== id), { shouldDirty: true });
    } else {
      form.setValue('servicios', [...current, id], { shouldDirty: true });
    }
  };

  const aplicarDatosHolded = (c: HoldedContactMatch) => {
    setLinkedHoldedContact(c);
    setIgnoreHoldedMatch(false);
    if (c.name) {
      form.setValue('nombre', c.tradeName || c.name);
      form.setValue('nombre_fiscal', c.name);
    }
    if (c.address) form.setValue('direccion', c.address);
    if (c.address) form.setValue('direccion_fiscal', c.address);
    if (c.postalCode) form.setValue('codigo_postal', c.postalCode);
    if (c.city) form.setValue('poblacion', c.city);
    if (c.email) {
      if (!form.getValues('email')) form.setValue('email', c.email);
      form.setValue('email_facturacion', c.email);
    }
    if (c.phone) {
      if (!form.getValues('telefono')) form.setValue('telefono', c.phone);
      form.setValue('telefono_facturacion', c.phone);
    }
    toast({ title: 'Datos importados de Holded', description: 'Revisa y completa lo que falte antes de guardar.' });
  };

  const construirPayloadCRA = (data: FormData) => {
    if (!data.servicios.includes('alarma')) return null;
    return {
      numero_abonado: data.numero_abonado?.trim() || null,
      palabra_clave: data.palabra_clave?.trim() || null,
      palabra_coaccion: data.palabra_coaccion?.trim() || null,
      contactos: (data.contactos || [])
        .filter((c) => c.nombre?.trim() || c.telefono?.trim())
        .map((c) => ({ nombre: c.nombre?.trim() || '', telefono: c.telefono?.trim() || '' })),
    };
  };

  const onSubmit = async (data: FormData) => {
    if (!isLoggedIn && !empresaId) {
      toast({ title: 'Error', description: 'No se pudo determinar la empresa.', variant: 'destructive' });
      return;
    }
    if (duplicadoLocal) {
      toast({ title: 'CIF/NIF duplicado', description: 'Ya existe un cliente con este NIF/CIF.', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      const nifValue = data.nif_cif?.trim() || null;
      const cra = construirPayloadCRA(data);

      const body: Record<string, any> = {
        nombre: data.nombre,
        nif_cif: nifValue,
        direccion: data.direccion || null,
        piso: data.piso || null,
        puerta: data.puerta || null,
        codigo_postal: data.codigo_postal || null,
        poblacion: data.poblacion || null,
        telefono: data.telefono || null,
        email: data.email || null,
        nombre_fiscal: data.nombre_fiscal || null,
        direccion_fiscal: data.direccion_fiscal || null,
        email_facturacion: data.email_facturacion || null,
        telefono_facturacion: data.telefono_facturacion || null,
        servicios: data.servicios,
        cra,
      };
      // Flujo público: empresa_id vía URL. Autenticado: la function lo deduce del JWT.
      if (!isLoggedIn) body.empresa_id = empresaId;
      if (isLoggedIn && linkedHoldedContact?.id) body.holded_contact_id = linkedHoldedContact.id;

      const { data: result, error } = await supabase.functions.invoke('public-alta-cliente', { body });
      if (error) throw error;
      if ((result as any)?.error) throw new Error((result as any).error);
      setCreatedClienteId((result as any)?.cliente_id || null);

      setSubmitted(true);
      toast({
        title: 'Cliente registrado',
        description: 'Cliente y sede principal creados correctamente.',
      });
    } catch (error: any) {
      console.error('Error al guardar cliente:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Hubo un error al guardar los datos del cliente.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4 md:p-6 flex items-center justify-center">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            {displayLogo && (
              <img src={displayLogo} alt={displayNombreEmpresa} className="max-h-16 w-auto object-contain mx-auto mb-2" />
            )}
            <CheckCircle className="h-16 w-16 mx-auto text-green-500" />
            <h2 className="text-2xl font-bold">¡Cliente registrado!</h2>
            <p className="text-muted-foreground">
              {isLoggedIn
                ? 'Cliente y sede principal creados. Si necesitas más sedes, añádelas desde la ficha.'
                : 'Los datos se han registrado correctamente. Gracias.'}
            </p>
            <div className="flex gap-2 justify-center pt-4 flex-wrap">
              {isLoggedIn && createdClienteId && (
                <Button onClick={() => navigate(`/cliente/${createdClienteId}`)}>
                  Ir al cliente →
                </Button>
              )}
              {isLoggedIn && (
                <Button variant="outline" onClick={() => {
                  setSubmitted(false); setCreatedClienteId(null); form.reset();
                  setLinkedHoldedContact(null); setIgnoreHoldedMatch(false); setDuplicadoLocal(null);
                }}>
                  Registrar otro cliente
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4 md:p-6">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Logo */}
        <div className="flex flex-col items-center justify-center space-y-3 pt-4">
          {displayLogo ? (
            <div className="bg-card p-5 rounded-2xl shadow-sm border">
              <img src={displayLogo} alt={displayNombreEmpresa} className="max-h-20 md:max-h-28 w-auto object-contain mx-auto" />
            </div>
          ) : displayNombreEmpresa ? (
            <div className="bg-primary/10 p-5 rounded-2xl">
              <Building2 className="h-14 w-14 text-primary" />
            </div>
          ) : null}
          {displayNombreEmpresa && (
            <p className="text-lg font-semibold text-primary/80">{displayNombreEmpresa}</p>
          )}
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b">
          <div className="flex items-center gap-4">
            {isLoggedIn && (
              <Button variant="ghost" size="icon" onClick={() => navigate('/clientes')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <div>
              <h1 className="text-3xl font-bold">Alta de Cliente</h1>
              <p className="text-muted-foreground mt-1">
                Se crea el cliente y su sede principal con los mismos datos. Los datos de CRA solo se piden si hay sistema de alarma.
              </p>
            </div>
          </div>
          {isLoggedIn && (
            <div className="flex-shrink-0">
              <ShareFormButton
                formType="alta-cliente-cra"
                title="Compartir Formulario Alta Cliente"
                description="Formulario para dar de alta un cliente en la plataforma"
              />
            </div>
          )}
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {/* Identificación fiscal */}
            <Card className="border-primary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck2 className="h-5 w-5 text-primary" />
                  Identificación fiscal
                </CardTitle>
                <CardDescription>
                  NIF / CIF único del cliente
                  {isLoggedIn && holdedActiva ? '. Se busca automáticamente en Holded para evitar duplicados.' : '.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <FormField
                  control={form.control}
                  name="nif_cif"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>NIF / CIF *</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="B12345678 ó 12345678A"
                            {...field}
                            onChange={(e) => {
                              field.onChange(e.target.value.toUpperCase());
                              if (linkedHoldedContact) setLinkedHoldedContact(null);
                              if (ignoreHoldedMatch) setIgnoreHoldedMatch(false);
                            }}
                            className="text-base font-mono uppercase tracking-wide pr-10"
                          />
                          {lookupStatus === 'searching' && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                          )}
                          {(lookupStatus === 'not_found' || lookupStatus === 'found' || linkedHoldedContact) && (
                            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {duplicadoLocal && (
                  <ClienteDuplicadoLocalCard clienteId={duplicadoLocal.id} nombre={duplicadoLocal.nombre} nifCif={duplicadoLocal.nif_cif} />
                )}
                {!duplicadoLocal && lookupEnabled && lookupStatus === 'found' && holdedContact && (
                  <HoldedContactMatchCard contact={holdedContact} onUseData={() => aplicarDatosHolded(holdedContact)} onIgnore={() => setIgnoreHoldedMatch(true)} />
                )}
                {!duplicadoLocal && linkedHoldedContact && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Enlazado al contacto de Holded "{linkedHoldedContact.name}"
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Servicios contratados */}
            <Card>
              <CardHeader>
                <CardTitle>Servicios contratados</CardTitle>
                <CardDescription>Marca los sistemas que tendrá este cliente. Si incluye alarma se pedirán los datos de CRA.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {SERVICIOS.map((s) => {
                    const checked = serviciosWatch.includes(s.id);
                    return (
                      <label
                        key={s.id}
                        className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer transition-colors ${
                          checked ? 'border-primary bg-primary/5' : 'hover:bg-muted/40'
                        }`}
                      >
                        <Checkbox checked={checked} onCheckedChange={() => toggleServicio(s.id)} />
                        <span className="text-sm font-medium">{s.label}</span>
                      </label>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Datos básicos */}
            <Card>
              <CardHeader>
                <CardTitle>Datos básicos</CardTitle>
                <CardDescription>Información comercial y de contacto general. Esta dirección será también la de la sede principal.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField control={form.control} name="nombre" render={({ field }) => (
                  <FormItem><FormLabel>Nombre comercial *</FormLabel><FormControl><Input placeholder="Nombre o razón social" {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="direccion" render={({ field }) => (
                  <FormItem><FormLabel>Dirección principal</FormLabel><FormControl><Input placeholder="Calle y número" {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="piso" render={({ field }) => (
                    <FormItem><FormLabel>Piso</FormLabel><FormControl><Input placeholder="2º" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="puerta" render={({ field }) => (
                    <FormItem><FormLabel>Puerta</FormLabel><FormControl><Input placeholder="A" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField control={form.control} name="codigo_postal" render={({ field }) => (
                    <FormItem><FormLabel>Código postal</FormLabel><FormControl><Input placeholder="28001" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="poblacion" render={({ field }) => (
                    <FormItem><FormLabel>Población</FormLabel><FormControl><Input placeholder="Madrid" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField control={form.control} name="telefono" render={({ field }) => (
                    <FormItem><FormLabel>Teléfono</FormLabel><FormControl><Input placeholder="612 345 678" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="email" render={({ field }) => (
                    <FormItem><FormLabel>Email</FormLabel><FormControl><Input type="email" placeholder="cliente@ejemplo.com" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                </div>
              </CardContent>
            </Card>

            {/* CRA (solo si alarma) */}
            {tieneAlarma && (
              <Card className="border-primary/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Radio className="h-5 w-5 text-primary" />
                    Datos de CRA
                  </CardTitle>
                  <CardDescription>
                    Información para la central receptora de alarmas. Se guardan en la sede principal.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField control={form.control} name="numero_abonado" render={({ field }) => (
                      <FormItem><FormLabel>Nº de abonado</FormLabel><FormControl><Input placeholder="AB-99001" {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="palabra_clave" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Palabra clave</FormLabel>
                        <FormControl><Input placeholder="La pide la CRA para verificar identidad" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="palabra_coaccion" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Palabra de coacción</FormLabel>
                        <FormControl><Input placeholder="Activa el protocolo si hay peligro" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-primary" />
                      <Label className="text-sm font-medium">Contactos autorizados (hasta 3)</Label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Personas a las que la CRA puede llamar ante una alarma.
                    </p>
                    {[0, 1, 2].map((idx) => (
                      <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 gap-3 border rounded-lg p-3 bg-card">
                        <FormField control={form.control} name={`contactos.${idx}.nombre` as const} render={({ field }) => (
                          <FormItem><FormLabel className="text-xs">Contacto {idx + 1} — Nombre</FormLabel><FormControl><Input placeholder="Juan López" {...field} /></FormControl><FormMessage /></FormItem>
                        )} />
                        <FormField control={form.control} name={`contactos.${idx}.telefono` as const} render={({ field }) => (
                          <FormItem><FormLabel className="text-xs">Teléfono</FormLabel><FormControl><Input placeholder="612 345 678" {...field} /></FormControl><FormMessage /></FormItem>
                        )} />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Datos facturación */}
            <Card>
              <CardHeader>
                <CardTitle>Datos de facturación</CardTitle>
                <CardDescription>Información fiscal para la facturación (opcional si coincide con los datos básicos).</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField control={form.control} name="nombre_fiscal" render={({ field }) => (
                  <FormItem><FormLabel>Nombre fiscal</FormLabel><FormControl><Input placeholder="Razón social o nombre fiscal" {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="direccion_fiscal" render={({ field }) => (
                  <FormItem><FormLabel>Dirección de facturación</FormLabel><FormControl><Textarea placeholder="Dirección de facturación..." {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormItem>
                    <FormLabel>NIF/CIF</FormLabel>
                    <FormControl>
                      <Input value={nifCifWatch || ''} readOnly disabled placeholder="Se toma de Identificación fiscal" className="bg-muted/40 cursor-not-allowed" />
                    </FormControl>
                    <p className="text-[11px] text-muted-foreground">Sincronizado con el NIF/CIF principal.</p>
                  </FormItem>
                  <FormField control={form.control} name="email_facturacion" render={({ field }) => (
                    <FormItem><FormLabel>Email de facturación</FormLabel><FormControl><Input type="email" placeholder="facturacion@ejemplo.com" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="telefono_facturacion" render={({ field }) => (
                    <FormItem><FormLabel>Teléfono de facturación</FormLabel><FormControl><Input placeholder="912 345 678" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-3">
              {isLoggedIn && (
                <Button type="button" variant="outline" onClick={() => navigate('/clientes')}>Cancelar</Button>
              )}
              <Button type="submit" size="lg" disabled={isSubmitting || !!duplicadoLocal} className="min-w-[150px]">
                {isSubmitting ? 'Guardando...' : 'Registrar cliente'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
